import { useState } from "react";

import { ActionBar } from "../../components/ActionBar";
import { Button } from "../../components/Button";
import { ConfirmSheet } from "../../components/ConfirmSheet";
import { Field } from "../../components/Fields";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Sheet } from "../../components/Sheet";
import { todayStr } from "../../domain/date";
import { familyWeek, LINK_READS } from "../../domain/family";
import { useLinked, useLinks } from "../../lib/linked";
import { useKaya } from "../../store/kayaStore";
import { api, type LinkInfo } from "../../sync/api";
import SettingsPage from "../settings/SettingsPage";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function who(l: LinkInfo): string {
  return l.label || l.person?.name || l.person?.email || "Someone";
}

/**
 * You › Family (ADR-046): the people whose plan this account reads, each a row to their page
 * (board FamilyView), the ones still waiting to accept, and the way to ask for another. The
 * other direction — who reads *you* — is on Account & data, beside the rest of where your data goes.
 */
export default function Family() {
  const { links, error, reload } = useLinks();
  const user = useKaya((s) => s.user);
  const showToast = useKaya((s) => s.showToast);
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [cancel, setCancel] = useState<LinkInfo | null>(null);

  const mine = (links ?? []).filter((l) => l.role === "reader");
  const active = mine.filter((l) => l.status === "active");
  const waiting = mine.filter((l) => l.status === "pending");
  const admin = user?.role === "admin";

  async function ask() {
    setBusy(true);
    try {
      const r = await api.linkAsk(email.trim(), label.trim());
      showToast(
        r.link.status === "active"
          ? `Linked — ${who(r.link)}'s phone shows it under Account & data`
          : `Asked — ${who(r.link)} accepts on their own phone`
      );
      setAdding(false);
      setEmail("");
      setLabel("");
      await reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not reach Kaya's server");
    } finally {
      setBusy(false);
    }
  }

  async function remove(l: LinkInfo) {
    try {
      await api.linkRemove(l.id);
      await reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not reach Kaya's server");
    }
    setCancel(null);
  }

  return (
    <SettingsPage
      title="Family"
      intro="People whose food and exercise plans you can read, and answer a few things for."
      onSave={() => setAdding(true)}
      dirty
      saveLabel="Link someone"
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {active.length ? (
        <RowGroup heading="People">
          {active.map((l) => (
            <PersonRow key={l.id} link={l} />
          ))}
        </RowGroup>
      ) : links ? (
        <Notice tone="info" icon={I.user}>
          Link a parent’s account to read their food plan, targets and exercise week, fill their eating and exercise
          answers, and set their water and dinner reminders. They sign in on their own phone, as themselves.
        </Notice>
      ) : null}

      {waiting.length ? (
        <RowGroup heading="Waiting">
          {waiting.map((l) => (
            <NavRow
              key={l.id}
              kind="action"
              icon={I.clock}
              title={who(l)}
              subtitle={`Accepts on their phone, under You › Account & data`}
              action={
                <Button variant="ghost" onClick={() => setCancel(l)}>
                  Cancel
                </Button>
              }
            />
          ))}
        </RowGroup>
      ) : null}

      <p className="you-intro">{LINK_READS}</p>

      {adding ? (
        <Sheet
          title="Link someone"
          sub={
            admin
              ? "As an admin you can already read their account, so the link is on at once. Their phone names it."
              : "They accept it on their own phone, under You › Account & data."
          }
          onClose={() => setAdding(false)}
          footer={
            <ActionBar
              inSheet
              secondary={{ label: "Cancel", onClick: () => setAdding(false) }}
              primary={{
                label: admin ? "Link" : "Ask",
                onClick: () => void ask(),
                busy,
                disabled: !EMAIL_RE.test(email.trim()),
                reason: "Their Kaya email first",
              }}
            />
          }
        >
          <div className="you-disclose-stack">
            <Field
              label="Their Kaya email"
              type="email"
              value={email}
              autoComplete="off"
              placeholder="the email they sign in with"
              onChange={setEmail}
            />
            <Field
              label="What you call them"
              type="text"
              value={label}
              placeholder="Mum"
              hint="Only you see this."
              onChange={setLabel}
            />
          </div>
        </Sheet>
      ) : null}

      {cancel ? (
        <ConfirmSheet
          title={`Stop asking ${who(cancel)}?`}
          confirm={{ label: "Stop asking", onClick: () => void remove(cancel) }}
          onClose={() => setCancel(null)}
        />
      ) : null}
    </SettingsPage>
  );
}

/** One linked person, with this week's plan as the row's line (EXAMPLE-DATA: "plan for this week
 *  · 4 of 7 days planned · protein short on 3 dinners"). */
function PersonRow({ link }: { link: LinkInfo }) {
  const { linked, error } = useLinked(link.id);
  const name = linked?.name ?? who(link);
  let sub = error ? "Could not load their plan" : "Loading…";
  if (linked) {
    if (!linked.synced) sub = "Nothing from their phone yet";
    else {
      const w = familyWeek(linked.S, todayStr());
      sub = [
        `Plan for this week · ${w.planned} of 7 days planned`,
        w.shortDinners ? `protein short on ${w.shortDinners} ${w.shortDinners === 1 ? "dinner" : "dinners"}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
  }
  return <NavRow icon={I.user} title={name} subtitle={sub} to={`/you/family/${link.id}`} />;
}
